// 3rd-party
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { DatePicker } from "antd";
import {
  Box,
  Button,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

// Config
import { appConfig } from "../../config/appConfig";

// Services
import { logout } from "../../services/IndiaService";
import { getStorageItemLocal } from "../../services/storageServices";

// Styles
import "./AuditTrials.scss";
import Spinner from "../../components/spinner/Spinner";

const { RangePicker } = DatePicker;

type SuperAdminEntry = {
  id: number;
  user: string;
  lastUpdated: string;
  actions: string;
  actionType: string;
};
type AdminEntry = {
  id: number;
  lastUpdated: string;
  actions: string;
};
interface userEntry {
  id: number;
  username: string;
}

const AuditTrials = () => {
  // useState
  const [superAdminDataList, setSuperAdminDataList] = useState<
    SuperAdminEntry[]
  >([]);
  const [adminDataList, setAdminDataList] = useState<AdminEntry[]>([]);
  const [filterList, setFilterList] = useState<any[]>([]);
  const [searchDate, setSearchDate] = useState<any>([]);
  const [searchActionType, setSearchActionType] = useState("");
  const [searchUser, setSearchUser] = useState("");
  const [role, setRole] = useState<string>("System Admin");
  const [userName, setUserName] = useState<userEntry[]>([]);
  const [page, setPage] = useState(0);
  const rowsPerPage = 7;
  const [loading, setloading] = useState(false);

  // useNavigate
  const navigate = useNavigate();

  // useEffect
  useEffect(() => {
    const storedRole = getStorageItemLocal("role");
    if (storedRole) setRole(JSON.parse(storedRole));
  }, []);

  useEffect(() => {
    if (role === "Admin User") {
      const storedUser = JSON.parse(
        localStorage.getItem("username") ?? "storedUser"
      );

      const AdminData = superAdminDataList.filter((item: any) => {
        return item.user === storedUser;
      });
      setAdminDataList(AdminData);
    }
  }, [superAdminDataList, role]);
  useEffect(() => {
    fetchData();
    fetchUser();
  }, []);
  useEffect(() => {
    let filteredData: any;

    if (role === "System Admin") {
      // For Super User
      filteredData = superAdminDataList.filter((item: any) => {
        const matchesUser = searchUser ? item.user === searchUser : true;
        const matchesActionType = (item.actions ?? "")
          .toLowerCase()
          .includes(searchActionType.trim().toLowerCase());
        const matchesDate1 = dayjs(item.lastUpdated).isSame(
          dayjs(searchDate[0]),
          "day"
        );
        const matchesDate2 = dayjs(item.lastUpdated).isAfter(
          dayjs(searchDate[0]),
          "day"
        );
        const matchesDate3 = dayjs(item.lastUpdated).isBefore(
          dayjs(searchDate[1]),
          "day"
        );
        const matchesDate4 = dayjs(item.lastUpdated).isSame(
          dayjs(searchDate[1]),
          "day"
        );

        if (searchDate.length > 0) {
          return (
            matchesUser &&
            matchesActionType &&
            (matchesDate1 || matchesDate2) &&
            (matchesDate3 || matchesDate4)
          );
        } else {
          return matchesUser && matchesActionType;
        }
      });
    } else {
      // For Admin User
      filteredData = adminDataList.filter((item: any) => {
        const matchesActionType = (item.actions ?? "")
          .toLowerCase()
          .includes(searchActionType.trim().toLowerCase());

        const matchesDate1 = dayjs(item.lastUpdated).isSame(
          dayjs(searchDate[0]),
          "day"
        );
        const matchesDate2 = dayjs(item.lastUpdated).isAfter(
          dayjs(searchDate[0]),
          "day"
        );
        const matchesDate3 = dayjs(item.lastUpdated).isBefore(
          dayjs(searchDate[1]),
          "day"
        );
        const matchesDate4 = dayjs(item.lastUpdated).isSame(
          dayjs(searchDate[1]),
          "day"
        );

        if (searchDate.length > 0) {
          return (
            matchesActionType &&
            (matchesDate1 || matchesDate2) &&
            (matchesDate3 || matchesDate4)
          );
        } else {
          return matchesActionType;
        }
      });
    }
    setFilterList(filteredData);
  }, [
    searchUser,
    searchActionType,
    searchDate,
    superAdminDataList,
    adminDataList,
  ]);

  const fetchData = async () => {
    setloading(true);
    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(appConfig.serviceUrls.saveAuditEntries, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 401) {
        logout(navigate);
        <Button onClick={() => logout(navigate)}>Logout</Button>;
        return;
      }
      if (!response.ok) throw new Error("Failed to fetch data");
      const data: SuperAdminEntry[] = await response.json();

      setSuperAdminDataList(data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setloading(false);
    }
  };
  const fetchUser = async () => {
    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(
        `${appConfig.serviceUrls.saveLoginEntries}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch User");
      const data = await response.json();
      const uniqueUsersMap = new Map();
      const uniqueUsers = data.filter((user: userEntry) => {
        if (!uniqueUsersMap.has(user.username)) {
          uniqueUsersMap.set(user.username, true);
          return true;
        }
        return false;
      });

      setUserName(uniqueUsers);
    } catch (error) {
      console.error("Error fetching user", error);
    }
  };
  return (
    <Box className="container-wraper">
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography className="bold-header-text">
          <b>Audit Trails</b>{" "}
        </Typography>
      </Box>

      <Stack direction="row" spacing={2} sx={{ mb: 2, mt: 1 }}>
        <RangePicker
          allowClear={false}
          value={searchDate}
          onChange={(e) => setSearchDate(e)}
        />
        {/* <DateRangePicker
            label="Search by Date"
            value={searchDate}
            onChange={(e) => setSearchDate(e)}
            sx={{ width: "250px", height: "55px", background: "#fff" }}
          /> */}
        <Button
          sx={{ width: "40px", height: "40px", background: "#fff" }}
          onClick={() => setSearchDate([])}
        >
          <ClearIcon />
        </Button>

        {role === "System Admin" && (
          <TextField
            select
            label="Search by User"
            size="small"
            value={searchUser}
            onChange={(e) => setSearchUser(e.target.value)}
            sx={{ width: "230px", height: "40px", background: "#fff" }}
          >
            <MenuItem value="">All</MenuItem>
            {userName.map((cat: userEntry) => (
              <MenuItem key={cat.id} value={cat.username}>
                {cat.username}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          select
          label="Search by Action"
          size="small"
          value={searchActionType}
          onChange={(e) => setSearchActionType(e.target.value)}
          // sx={{ minWidth: 180 }}
          sx={{ width: "230px", height: "40px", background: "#fff" }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Add">Added</MenuItem>
          <MenuItem value="Updated">Updated</MenuItem>
          <MenuItem value="Publish">Published</MenuItem>
          <MenuItem value="Delete">Deleted</MenuItem>
          <MenuItem value="Reject">Rejected</MenuItem>
        </TextField>
      </Stack>

      {loading ? (
        <>
          {" "}
          <Spinner></Spinner>
        </>
      ) : (
        <></>
      )}

      <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
        {/*Data Table*/}

        <TableContainer
          component={Paper}
          sx={{
            // padding: 2,
            width: "100%",
            // maxHeight: "80vh",
            // overflow:"auto",
            // "&::-webkit-scrollbar":{display:"none"},
            // msOverflowStyle:"none",
            // scrollbarWidth:"none",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow className="custom-table-head-row">
                <TableCell>
                  <b>Date & Time</b>
                </TableCell>
                {role === "System Admin" && (
                  <TableCell>
                    <b>User</b>
                  </TableCell>
                )}

                <TableCell>
                  <b>Actions</b>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filterList
                ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item, index) => (
                  <TableRow key={index} className="custom-table-row">
                    <TableCell>
                      {dayjs(item.lastUpdated).format("MMM DD, YYYY hh:mm A") ||
                        "\u00A0"}
                    </TableCell>
                    {role === "System Admin" && (
                      <TableCell>{item.user || "\u00A0"}</TableCell>
                    )}
                    <TableCell>{item.actions || "\u00A0"}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>

          <Box
            display="flex"
            justifyContent="space-between"
            p={2}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "1rem",
            }}
          >
            <Typography
              sx={{
                color: "#000",
                fontFamily: "Inter",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "normal",
              }}
            >
              Showing {page * rowsPerPage + 1} to{" "}
              {page * rowsPerPage + rowsPerPage} of {filterList.length} entries
            </Typography>
            <Pagination
              count={Math.ceil(filterList.length / rowsPerPage)}
              page={page + 1}
              onChange={(e, value) => setPage(value - 1)}
              color="primary"
              variant="outlined"
              shape="rounded"
            />
          </Box>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default AuditTrials;
